import { FlexBox, Icon, Text, Tooltip } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { useMemo } from 'react';

import { Channel } from '../../richTextField';

import { CharacterLimitIndicator } from './characterLimitIndicator';
import styles from './stateBar.module.scss';
import { ValidationState } from './stateBar.types';
import { useCharacterCounter } from './utils/useCharacterCounter/useCharacterCounter';

type StateBarProps = {
  /** The channel. */
  channel: Channel;
  /** The text. */
  text: string;
  /** The validation state. */
  validation: ValidationState;
  className?: string;
  isPublicReply?: boolean;
};

const channelsWithCharacterLimitIndicator: Channel[] = ['twitter', 'whatsapp'];

/**
 * Represents a component that prints the validation error/warning messages as
 * well as character counting.
 */
export const StateBar = ({
  channel,
  text,
  validation,
  className,
  isPublicReply,
}: StateBarProps): JSX.Element => {
  const { count, limit, messages, encoding } = useCharacterCounter({
    channel,
    text,
    isPublicReply,
  });

  const errorMessage = useMemo(() => {
    if (count == 0 && validation.touched) {
      return 'Required';
    }

    if (count > limit) {
      return `${count}/${limit} · Your message is too long`;
    }
  }, [count, limit, validation.touched]);

  const warningMessage = useMemo(() => {
    if (validation.touched && validation.warning) {
      switch (validation.warning.type) {
        case 'offline':
          return 'The customer is offline';
        case 'file-count':
          return `${validation.warning.arg.count}/${validation.warning.arg.maxCount} · File count is too large`;
        case 'file-size':
          return `${validation.warning.arg.size}/${validation.warning.arg.maxSize} · File size is too large`;
        case 'file-type':
          return `${validation.warning.arg.fileType} · File type is not supported. Supported types: ${validation.warning.arg.supportedFileTypes.map((type) => `.${type}`).join(', ')}`;
      }
    }
  }, [validation.touched, validation.warning]);

  const messageColor = useMemo(() => {
    if (errorMessage) {
      return colors.red[600];
    }
    if (warningMessage) {
      return colors.orange[400];
    }
  }, [errorMessage, warningMessage]);

  return (
    <FlexBox
      className={classnames(styles.root, className)}
      alignItems="center"
      gap="200"
    >
      {channel === 'sms' && encoding === 'UTF-16' && (
        <Tooltip
          className={styles.tooltip}
          content="Special characters, such as emojis or non-Latin characters, reduce the character limit for text messages from 160 to 70, which can increase the overall text message count."
        >
          <FlexBox>
            <Icon name="info" color={messageColor} />
          </FlexBox>
        </Tooltip>
      )}

      {warningMessage && (
        <Text.Paragraph variant="2" subdued color={colors.orange[400]}>
          {warningMessage}
        </Text.Paragraph>
      )}

      {errorMessage && (
        <Text.Paragraph variant="2" subdued color={colors.red[600]}>
          {errorMessage}
        </Text.Paragraph>
      )}

      {!errorMessage && !warningMessage && channel === 'sms' && (
        <Text.Paragraph variant="2" subdued>
          {`${count}/${limit} · ${messages} text ${
            messages === 1 ? 'message' : 'messages'
          }`}
        </Text.Paragraph>
      )}

      {!errorMessage &&
        !warningMessage &&
        channelsWithCharacterLimitIndicator.includes(channel) && (
          <CharacterLimitIndicator count={count} limit={limit} />
        )}
    </FlexBox>
  );
};
